import React, { useState, useEffect } from 'react';
import { collection, getDocs, onSnapshot, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import MonthlyView from './MonthlyView';
import ExcelImport from './ExcelImport';
import WorkersManager from './WorkersManager';
import LocationsManager from './LocationsManager';
import Settings from './Settings';
import WhatsAppManager from './WhatsAppManager';

// ── Helpers ───────────────────────────────────────────────────────────────────
function todayIST() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}
function formatDate(str) {
  return new Date(str + 'T00:00:00+05:30').toLocaleDateString('en-IN', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });
}

const BOT_URL = process.env.REACT_APP_BOT_URL || 'http://localhost:8080';

// ── Photo Modal ───────────────────────────────────────────────────────────────
function PhotoModal({ record, onClose }) {
  if (!record) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>📍 {record.locationId} — {record.locationName || record.locationId}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-info">
          <div className="modal-row">
            <span className="modal-label">👤 Worker</span>
            <span className="modal-value">{record.workerName} ({record.workerId})</span>
          </div>
          <div className="modal-row">
            <span className="modal-label">📅 Date</span>
            <span className="modal-value">{record.date}</span>
          </div>
          <div className="modal-row">
            <span className="modal-label">⏰ Time</span>
            <span className="modal-value">{record.submittedTime}</span>
          </div>
          <div className="modal-row">
            <span className="modal-label">📲 Method</span>
            <span className="modal-value" style={{textTransform:'capitalize'}}>{record.method}</span>
          </div>
        </div>
        {record.photoFileId ? (
          <a
            className="modal-photo-link"
            href={`${BOT_URL}/admin/photo/${record.photoFileId}`}
            target="_blank" rel="noreferrer"
          >
            📸 View Photo (Telegram)
          </a>
        ) : record.photoData ? (
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <img 
              src={record.photoData} 
              alt="Manual Submission" 
              style={{ maxWidth: '100%', maxHeight: '380px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.12)' }} 
            />
          </div>
        ) : record.photoUrl ? (
          <a
            className="modal-photo-link"
            href={record.photoUrl}
            target="_blank" rel="noreferrer"
          >
            📸 View Photo Link
          </a>
        ) : (
          <div className="no-photo">No photo available</div>
        )}
      </div>
    </div>
  );
}

// ── Location Card ─────────────────────────────────────────────────────────────
function LocationCard({ locationId, locationName, priority, record, hourNow, onView, onMarkManual }) {
  const submitted = !!record;
  const late = !submitted && hourNow >= 11;
  const cls = (submitted ? 'submitted' : late ? 'late' : 'pending') + (priority ? ' priority-card' : '');
  const dotCls = submitted ? 'green' : late ? 'yellow' : 'red';
  const statusText = submitted ? `✅ ${record.submittedTime}` : late ? '⚠️ Late / Pending' : '🔴 Pending';

  return (
    <div 
      className={`location-card ${cls}`} 
      onClick={() => submitted ? onView(record) : onMarkManual(locationId, locationName)}
      style={{ cursor: 'pointer' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <div className="loc-id" style={{ marginBottom: 0 }}>{locationId}</div>
        {priority && (
          <span className="priority-badge" style={{
            fontSize: '10px',
            fontWeight: '700',
            background: 'rgba(245, 158, 11, 0.15)',
            color: '#fbbf24',
            padding: '2px 8px',
            borderRadius: '12px',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            ⭐ {priority}
          </span>
        )}
      </div>
      <div className="loc-name">{locationName}</div>
      <div className="loc-status">
        <span className={`status-dot ${dotCls}`}></span>
        <span style={{color: submitted ? '#34d399' : late ? '#fbbf24' : '#f87171'}}>
          {statusText}
        </span>
      </div>
      {submitted ? (
        <>
          <div className="loc-worker">👤 {record.workerName}</div>
          {(record.photoFileId || record.photoData || record.photoUrl) && (
            <button className="view-photo-btn" onClick={e => { e.stopPropagation(); onView(record); }}>
              📸 Photo
            </button>
          )}
        </>
      ) : (
        <div style={{ marginTop: '12px', textAlign: 'right' }}>
          <span style={{ fontSize: '11px', color: '#60a5fa', fontWeight: '600' }}>
            ➕ Mark Manual
          </span>
        </div>
      )}
    </div>
  );
}

// ── Manual Attendance Modal ──────────────────────────────────────────────────
function ManualAttendanceModal({ locationId, locationName, date, onClose }) {
  const [workers, setWorkers] = useState([]);
  const [selectedWorkerId, setSelectedWorkerId] = useState('');
  const [photoData, setPhotoData] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const snap = await getDocs(collection(db, 'workers'));
        const list = [];
        snap.forEach(doc => {
          const data = doc.data();
          if (data.active !== false) {
            list.push({ id: doc.id, name: data.name, locations: data.locations || [] });
          }
        });
        
        // Sort: Workers assigned to this location at the top, then alphabetically
        list.sort((a, b) => {
          const aAssigned = a.locations.includes(locationId);
          const bAssigned = b.locations.includes(locationId);
          if (aAssigned && !bAssigned) return -1;
          if (!aAssigned && bAssigned) return 1;
          return a.name.localeCompare(b.name);
        });
        
        setWorkers(list);
        if (list.length > 0) {
          setSelectedWorkerId(list[0].id);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchWorkers();
  }, [locationId]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2000000) {
        setError('❌ Image size 2MB se kam honi chahiye!');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoData(reader.result);
        setError('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!selectedWorkerId) {
      setError('❌ Kripya worker select karein!');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const savedPassword = sessionStorage.getItem('eco_admin_pw') || 'Ecoholic@2025';
      const res = await fetch(`${BOT_URL}/api/manual-attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          adminPassword: savedPassword,
          date,
          locationId,
          workerId: selectedWorkerId,
          photoData: photoData || null,
        }),
      });

      const data = await res.json();
      if (res.status === 200 && data.success) {
        onClose();
      } else {
        if (res.status === 401) {
          sessionStorage.removeItem('eco_admin');
          sessionStorage.removeItem('eco_admin_pw');
          window.location.reload();
          return;
        }
        setError('❌ Saving failed: ' + (data.error || 'Server error'));
      }
    } catch (err) {
      console.error(err);
      setError('❌ Saving failed: ' + err.message);
    }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: '460px' }}>
        <div className="modal-header">
          <h3>➕ Manual Attendance: {locationId}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '16px' }}>
          📍 {locationName}
        </p>

        <div style={{ display: 'grid', gap: '16px', marginTop: '8px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>
              Select Worker *
            </label>
            <select
              value={selectedWorkerId}
              onChange={e => setSelectedWorkerId(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: '#0f172a',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '8px',
                color: '#f8fafc',
                fontSize: '14px',
                outline: 'none',
              }}
            >
              {workers.map(w => {
                const isAssigned = w.locations.includes(locationId);
                return (
                  <option key={w.id} value={w.id}>
                    {w.name} ({w.id}){isAssigned ? ' 📍 [Assigned]' : ''}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>
              Upload Attendance Photo (Optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{
                width: '100%',
                padding: '8px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                color: '#94a3b8',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            />
          </div>

          {photoData && (
            <div style={{ textAlign: 'center', marginTop: '8px' }}>
              <img
                src={photoData}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
          )}

          {error && (
            <div style={{ color: '#f87171', fontSize: '13px', marginTop: '8px' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                flex: 1,
                padding: '12px',
                background: saving ? 'rgba(52,211,153,0.3)' : 'linear-gradient(135deg,#34d399,#059669)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontWeight: '600',
                cursor: saving ? 'default' : 'pointer',
              }}
            >
              {saving ? '⏳ Saving...' : '💾 Save Attendance'}
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '12px 20px',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '8px',
                color: '#94a3b8',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Today's View ──────────────────────────────────────────────────────────────
function TodayView() {
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(todayIST());
  const [modal, setModal] = useState(null);
  const [manualModal, setManualModal] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loadingLocs, setLoadingLocs] = useState(true);

  // States for PPTX downloader progress
  const [generatingPPTX, setGeneratingPPTX] = useState(false);
  const [pptxProgress, setPptxProgress] = useState(0);
  const [pptxTotal, setPptxTotal] = useState(0);

  const fetchLocations = async () => {
    setLoadingLocs(true);
    try {
      const snap = await getDocs(collection(db, 'locations'));
      const locList = [];
      snap.forEach(doc => {
        const data = doc.data();
        if (data.active !== false) {
          locList.push({ 
            id: doc.id, 
            locationName: data.locationName || doc.id,
            priority: data.priority || '',
            // Fetch store contact number
            phone: data.storeManagerContact || data.clusterPhone || ''
          });
        }
      });
      locList.sort((a, b) => {
        const numA = parseInt((a.id || '').replace(/\D/g, '') || 0);
        const numB = parseInt((b.id || '').replace(/\D/g, '') || 0);
        return numA - numB;
      });
      setLocations(locList);
    } catch (e) {
      console.error('Failed to fetch locations:', e);
    }
    setLoadingLocs(false);
  };

  const fetchAttendance = async (date) => {
    setLoading(true);
    try {
      const snap = await getDocs(
        collection(db, 'attendance', date, 'locations')
      );
      const map = {};
      snap.forEach(doc => { map[doc.id] = doc.data(); });
      setAttendance(map);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  // Real-time listener for dynamic attendance updates
  useEffect(() => {
    setLoading(true);
    const colRef = collection(db, 'attendance', selectedDate, 'locations');
    const unsubscribe = onSnapshot(colRef, (snap) => {
      const map = {};
      snap.forEach(doc => { map[doc.id] = doc.data(); });
      setAttendance(map);
      setLoading(false);
    }, (err) => {
      console.error('Real-time attendance fetch error:', err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [selectedDate]);

  // Helper to fetch any remote image and convert it to base64
  const getBase64Image = async (record) => {
    if (record.photoData) {
      return record.photoData; // Already base64 from manual uploads
    }

    let imageUrl = '';
    if (record.photoFileId) {
      imageUrl = `${BOT_URL}/admin/photo/${record.photoFileId}`;
    } else if (record.photoUrl) {
      imageUrl = record.photoUrl;
    }

    if (!imageUrl) return null;

    try {
      const res = await fetch(imageUrl);
      if (!res.ok) throw new Error(`HTTP status ${res.status}`);
      const blob = await res.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.warn("Failed to fetch photo as base64, using direct URL path as fallback:", err);
      return imageUrl; // Fallback to raw URL path
    }
  };

  // Helper to dynamically load pptxgenjs from CDN to avoid Webpack 5 bundling issues
  const loadPptxGen = () => {
    return new Promise((resolve, reject) => {
      if (window.PptxGenJS) {
        resolve(window.PptxGenJS);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/pptxgenjs@4.0.1/dist/pptxgen.bundle.js';
      script.onload = () => {
        if (window.PptxGenJS) {
          resolve(window.PptxGenJS);
        } else {
          reject(new Error("pptxgenjs loaded but PptxGenJS global not found"));
        }
      };
      script.onerror = () => reject(new Error("Failed to load pptxgenjs from CDN"));
      document.body.appendChild(script);
    });
  };

  // PowerPoint Report Downloader Function
  const downloadPPTX = async () => {
    // Filter active locations that have submitted attendance with photos
    const submittedList = locations.filter(loc => {
      const rec = attendance[loc.id];
      return rec && (rec.photoFileId || rec.photoData || rec.photoUrl);
    });

    if (submittedList.length === 0) {
      alert("❌ Selected date ke liye koi photo attendance submit nahi mili.");
      return;
    }

    setGeneratingPPTX(true);
    setPptxProgress(0);
    setPptxTotal(submittedList.length);

    try {
      const PptxGenJS = await loadPptxGen();
      const pptx = new PptxGenJS();
      pptx.title = `Ecoholic Elements Daily Report - ${selectedDate}`;
      pptx.layout = 'LAYOUT_16x9';

      for (let i = 0; i < submittedList.length; i++) {
        const loc = submittedList[i];
        const rec = attendance[loc.id];
        setPptxProgress(i + 1);

        const slide = pptx.addSlide();
        
        // Sleek premium dark theme matching the dashboard
        slide.background = { color: '0f172a' }; 

        // 1. Header Branding
        slide.addText("🌱 ECOHOLIC ELEMENTS", {
          x: 0.5, y: 0.4, w: 9.0, h: 0.4,
          fontSize: 18, color: '10b981', bold: true, fontFace: 'Arial'
        });
        
        slide.addText(`DAILY ATTENDANCE REPORT — ${formatDate(selectedDate)}`, {
          x: 0.5, y: 0.7, w: 9.0, h: 0.3,
          fontSize: 11, color: '64748b', fontFace: 'Arial'
        });

        // 2. Info Section (Left side) - Only Location ID, Name, Contact Phone, Time
        slide.addText(loc.id, {
          x: 0.5, y: 1.5, w: 4.5, h: 0.7,
          fontSize: 42, color: '38bdf8', bold: true, fontFace: 'Arial'
        });

        slide.addText(`Location Name:`, {
          x: 0.5, y: 2.3, w: 4.5, h: 0.25,
          fontSize: 11, color: '94a3b8', fontFace: 'Arial'
        });
        slide.addText(loc.locationName, {
          x: 0.5, y: 2.55, w: 4.5, h: 0.8,
          fontSize: 18, color: 'f8fafc', bold: true, fontFace: 'Arial',
          valign: 'top'
        });

        const contactNum = loc.phone || 'Not Registered';
        slide.addText(`Contact Phone:`, {
          x: 0.5, y: 3.5, w: 4.5, h: 0.25,
          fontSize: 11, color: '94a3b8', fontFace: 'Arial'
        });
        slide.addText(contactNum, {
          x: 0.5, y: 3.75, w: 4.5, h: 0.4,
          fontSize: 18, color: 'fbbf24', bold: true, fontFace: 'Arial'
        });

        slide.addText(`Submission Details:`, {
          x: 0.5, y: 4.3, w: 4.5, h: 0.25,
          fontSize: 11, color: '94a3b8', fontFace: 'Arial'
        });
        slide.addText(`Submitted at ${rec.submittedTime} (${rec.method})`, {
          x: 0.5, y: 4.55, w: 4.5, h: 0.4,
          fontSize: 13, color: '34d399', fontFace: 'Arial'
        });

        // Thin visual divider
        slide.addShape('rect', {
          x: 0.5, y: 5.2, w: 4.0, h: 0.02,
          fill: { color: '334155' }
        });

        // 3. Photo Section (Right side, centered container)
        const photoData = await getBase64Image(rec);
        if (photoData) {
          const imgOpts = {
            x: 5.2, y: 1.1, w: 4.3, h: 4.3,
            sizing: { type: 'contain', w: 4.3, h: 4.3 }
          };
          if (photoData.startsWith('data:')) {
            imgOpts.data = photoData;
          } else {
            imgOpts.path = photoData;
          }
          slide.addImage(imgOpts);
        } else {
          slide.addText("📷 Photo loading failed or not available", {
            x: 5.2, y: 2.8, w: 4.3, h: 0.5,
            fontSize: 14, color: 'f87171', align: 'center', fontFace: 'Arial'
          });
        }
      }

      await pptx.writeFile({ fileName: `Ecoholic_Attendance_${selectedDate}.pptx` });
    } catch (err) {
      console.error("PPTX Generation Error:", err);
      alert(`❌ Presentation save karne mein error occurred: ${err.message}`);
    } finally {
      setGeneratingPPTX(false);
    }
  };

  const hourNow = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', hour12: false });
  const isProgress = loading || loadingLocs;
  const submitted = Object.keys(attendance).filter(id => locations.some(loc => loc.id === id)).length;
  const pending = locations.length - submitted;
  const completionRate = locations.length > 0 ? Math.round((submitted / locations.length) * 100) : 0;

  return (
    <>
      {modal && <PhotoModal record={modal} onClose={() => setModal(null)} />}

      <div className="stats-bar">
        <div className="stat-card green">
          <div className="label">Submitted</div>
          <div className="value">{submitted}</div>
        </div>
        <div className="stat-card red">
          <div className="label">Pending</div>
          <div className="value">{pending}</div>
        </div>
        <div className="stat-card blue">
          <div className="label">Total Locations</div>
          <div className="value">{locations.length}</div>
        </div>
        <div className="stat-card yellow">
          <div className="label">Completion</div>
          <div className="value">{completionRate}%</div>
        </div>
      </div>

      <div className="section-header">
        <h2>📍 Today's Attendance</h2>
        <div style={{display:'flex', gap:'12px', alignItems:'center'}}>
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            style={{
              background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.15)',
              borderRadius:'8px', padding:'6px 12px', color:'#e2e8f0', fontSize:'13px',
              outline:'none', cursor:'pointer'
            }}
          />
          <div className="date-badge">{formatDate(selectedDate)}</div>
          <button
            onClick={() => fetchAttendance(selectedDate)}
            style={{
              background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.1)',
              borderRadius:'8px', padding:'6px 14px', color:'#94a3b8',
              cursor:'pointer', fontSize:'13px'
            }}
          >🔄 Refresh</button>
          
          <button
            onClick={downloadPPTX}
            disabled={generatingPPTX}
            style={{
              background: 'linear-gradient(135deg, #10b981, #059669)',
              border: 'none',
              borderRadius: '8px',
              padding: '6px 14px',
              color: '#fff',
              fontWeight: '600',
              cursor: generatingPPTX ? 'default' : 'pointer',
              fontSize: '13px',
              opacity: generatingPPTX ? 0.6 : 1,
              transition: 'opacity 0.2s',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {generatingPPTX ? `⏳ Generating (${pptxProgress}/${pptxTotal})` : '📥 Download PPTX Slides'}
          </button>
        </div>
      </div>

      {isProgress ? (
        <div className="loading"><div className="spinner"></div>Loading attendance...</div>
      ) : (
        <div className="attendance-grid">
          {locations.map(loc => (
            <LocationCard
              key={loc.id}
              locationId={loc.id}
              locationName={loc.locationName}
              priority={loc.priority}
              record={attendance[loc.id] || null}
              hourNow={parseInt(hourNow)}
              onView={setModal}
              onMarkManual={(id, name) => setManualModal({ id, name })}
            />
          ))}
        </div>
      )}

      {manualModal && (
        <ManualAttendanceModal
          locationId={manualModal.id}
          locationName={manualModal.name}
          date={selectedDate}
          onClose={() => setManualModal(null)}
        />
      )}
    </>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function Dashboard({ onLogout }) {
  const [view, setView] = useState('today');

  const navItems = [
    { id: 'today',     icon: '📊', label: "Today's View" },
    { id: 'monthly',   icon: '📅', label: 'Monthly Report' },
    { id: 'locations', icon: '📍', label: 'Locations' },
    { id: 'workers',   icon: '👥', label: 'Workers' },
    { id: 'import',    icon: '📂', label: 'Excel Import' },
    { id: 'whatsapp',  icon: '💬', label: 'WhatsApp Bot' },
    { id: 'settings',  icon: '⚙️', label: 'Profile Settings' },
  ];

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <h2>🌱 Ecoholic</h2>
          <p>Attendance Admin</p>
        </div>
        <nav className="sidebar-nav">
          {navItems.map(item => (
            <div
              key={item.id}
              className={`nav-item ${view === item.id ? 'active' : ''}`}
              onClick={() => setView(item.id)}
            >
              <span className="icon">{item.icon}</span>
              {item.label}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <button className="logout-btn" onClick={onLogout}>↩ Logout</button>
        </div>
      </aside>

      <main className="main-content">
        {view === 'today'     && <TodayView key="today" />}
        {view === 'monthly'   && <MonthlyView key="monthly" />}
        {view === 'locations' && <LocationsManager key="locations" />}
        {view === 'workers'   && <WorkersManager key="workers" />}
        {view === 'import'    && <ExcelImport key="import" />}
        {view === 'whatsapp'  && <WhatsAppManager key="whatsapp" />}
        {view === 'settings'  && <Settings key="settings" />}
      </main>
    </div>
  );
}
